import { ReactElement } from 'react'
import { useWatch, useController, useFormContext } from 'react-hook-form'
import { Stack, Typography } from '@mui/material'
import { CheckboxLabeled } from '@ui/actions'
import { InfoTooltip } from '@ui/feedback'
import {
  CheckSettingsFormFieldsCommonProps,
  compactCheckSettingsFormSx,
  getCheckSettingsMaxWidth,
  getCheckSettingsStackSpacing,
} from './checkSettingsFormFieldLayout'
import { DefaultValues } from './types'

const MEMBER_SETTINGS_ROOT = 'memberCheckSettingsSelectedElement' as const

interface Props extends CheckSettingsFormFieldsCommonProps {
  memberCheckSettingsSelectedPath?: string
}

const ShearCheckSettingsFormFields = ({
  disabled,
  compact,
  memberCheckSettingsSelectedPath = MEMBER_SETTINGS_ROOT,
}: Props): ReactElement | null => {
  const memberCheckSettingsSelectedElement: SettingsOnMember = useWatch<DefaultValues>({
    name: memberCheckSettingsSelectedPath as 'memberCheckSettingsSelectedElement',
  })

  if (!('shear_check_settings' in memberCheckSettingsSelectedElement))
    throw new Error('Shear check settings not available')

  // Wrapper component to integrate CheckboxLabeled with react-hook-form
  const FormCheckboxLabeled = ({
    name,
    label,
    tooltip,
    'data-cy': dataCy,
    disabled: checkboxDisabled,
  }: {
    name: string
    label: string
    tooltip?: { text: string; secondLineText?: string }
    'data-cy'?: string
    disabled?: boolean
  }) => {
    const { control } = useFormContext()
    const {
      field: { onChange, value },
    } = useController({ name, control })

    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CheckboxLabeled
          checked={value}
          onChange={onChange}
          label={label}
          data-cy={dataCy}
          disabled={checkboxDisabled}
        />
        {tooltip && <InfoTooltip {...tooltip} />}
      </Stack>
    )
  }

  return (
    <Stack
      direction="column"
      spacing={getCheckSettingsStackSpacing(compact)}
      maxWidth={getCheckSettingsMaxWidth(compact)}
      sx={compact ? compactCheckSettingsFormSx : undefined}
    >
      <FormCheckboxLabeled
        name={`${memberCheckSettingsSelectedPath}.shear_check_settings.reduce_point_loads_close_to_supports`}
        label="Auflagernahe Punktlasten abmindern"
        disabled={disabled}
        tooltip={{
          text: 'der Querkraftanteil aus Punktlasten innerhalb des Abstands h zu Auflagern bleibt unberücksichtigt',
          secondLineText: 'nicht erlaubt, wenn Ausklinkung am Auflager.',
        }}
        data-cy="shear-check-reduce-point-loads-checkbox"
      />

      <FormCheckboxLabeled
        name={`${memberCheckSettingsSelectedPath}.shear_check_settings.reduce_shear_force`}
        label="Bemessungsquerkraft im Abstand h vom Auflager"
        disabled={disabled}
        tooltip={{
          text: 'die Bemessungsquerkraft wird im Abstand h zu Auflagern bestimmt anstatt in der rechnerischen Auflagerlinie',
          secondLineText:
            'nur erlaubt, wenn Querkräfte an Oberseite des Trägers angreifen (z. B. nicht bei anschließenden Schwalbenschwänzen).',
        }}
      />

      <Typography sx={compact ? { fontSize: 11, lineHeight: 1.35 } : undefined}>
        Die hier eingestellte Querkraftabminderung wird für die kalte UND die heiße Bemessung
        angewendet, falls die Heißbemessung aktiviert ist.
      </Typography>
    </Stack>
  )
}

export default ShearCheckSettingsFormFields
